import {ChangeDetectionStrategy, Component, Input} from '@angular/core';
import {HeatMapV2Point} from '@core/types';

const W = 520, H = 520;
const M = {t: 16, r: 20, b: 46, l: 48};
const PW = W - M.l - M.r;
const PH = H - M.t - M.b;

const RISK_LEVELS = [
  {k: 0,  kNext: 9,   color: '#97C459', bg: '#EAF3DE', textColor: '#27500A', label: 'Muy bajo', range: '≤ 9'},
  {k: 9,  kNext: 22,  color: '#1D9E75', bg: '#E1F5EE', textColor: '#085041', label: 'Bajo',     range: '10–22'},
  {k: 22, kNext: 42,  color: '#EF9F27', bg: '#FAEEDA', textColor: '#633806', label: 'Estable',  range: '23–42'},
  {k: 42, kNext: 70,  color: '#E24B4A', bg: '#FCEBEB', textColor: '#791F1F', label: 'Alto',     range: '43–70'},
  {k: 70, kNext: 1e9, color: '#534AB7', bg: '#EEEDFE', textColor: '#3C3489', label: 'Muy alto', range: '> 70'},
];

const AXIS_TICKS = [0, 20, 40, 60, 80, 100];
const DEFAULT_COLOR = '#5C5A66';
const LABEL_OFFSET = 11;

function svgX(impact: number): number {
  return M.l + PW * impact / 100;
}

function svgY(probability: number): number {
  return M.t + PH * (1 - probability / 100);
}

function curvePts(k: number): [number, number][] {
  const pts: [number, number][] = [];
  for (let I = Math.max(k, 0.5); I <= 100; I += 0.5) {
    const P = (k * 100) / I;
    if (P > 100) continue;
    pts.push([svgX(I), svgY(P)]);
  }
  return pts;
}

function buildZonePath(kLo: number, kHi: number): string {
  const topLeft: [number, number] = [svgX(0), svgY(100)];

  let d = `M${topLeft[0]},${topLeft[1]}`;

  if (kHi <= 100) {
    const upper = curvePts(kHi);
    d += ` L${svgX(kHi).toFixed(1)},${svgY(100).toFixed(1)}`;
    d += ' L' + upper.map(p => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' L');
  } else {
    d += ` L${svgX(100).toFixed(1)},${svgY(100).toFixed(1)}`;
  }

  if (kLo > 0) {
    const lower = curvePts(kLo).slice().reverse();
    d += ' L' + lower.map(p => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' L');
    d += ` L${svgX(kLo).toFixed(1)},${svgY(100).toFixed(1)} Z`;
  } else {
    d += ` L${svgX(100).toFixed(1)},${svgY(0).toFixed(1)} L${svgX(0).toFixed(1)},${svgY(0).toFixed(1)} Z`;
  }

  return d;
}

function buildBorderPath(k: number): string {
  const pts = curvePts(k);
  return 'M' + pts.map(p => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' L');
}

const ZONE_PATHS: {d: string; color: string}[] = RISK_LEVELS.map(lv =>
  ({d: buildZonePath(lv.k, lv.kNext), color: lv.color})
);

const BORDER_PATHS: string[] = [9, 22, 42, 70].map(k => buildBorderPath(k));

export interface RenderedV2Point {
  key: string;
  cx: number;
  cyInh: number;
  cyRes: number | null;
  color: string;
  label: string;
  tooltip: string;
  labelX: number;
  labelY: number;
  textAnchor: string;
}

@Component({
  selector: 'core-heat-map-widget-v2',
  imports: [],
  templateUrl: './heat-map-widget-v2.html',
  styleUrl: './heat-map-widget-v2.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {style: 'display: block; width: 100%;'},
})
export class HeatMapWidgetV2 {
  @Input() labelX = 'Impacto';
  @Input() labelY = 'Probabilidad';
  @Input() points: HeatMapV2Point[] = [];
  @Input() showInherited = true;
  @Input() showResidual = true;

  readonly zonePaths = ZONE_PATHS;
  readonly borderPaths = BORDER_PATHS;
  readonly riskLevels = RISK_LEVELS;
  readonly axisTicks = AXIS_TICKS;
  readonly svgWidth = W;
  readonly svgHeight = H;
  readonly margin = M;

  get renderedPoints(): RenderedV2Point[] {
    return (this.points || []).map((point, index) => {
      const cx = svgX(point.impact);
      const cyInh = svgY(point.probability);
      const cyRes = point.residualProbability != null ? svgY(point.residualProbability) : null;
      const color = point.color || DEFAULT_COLOR;
      const side = index % 2 === 0 ? 1 : -1;
      const labelX = cx + side * LABEL_OFFSET;
      const anchorY = cyRes != null ? cyRes : cyInh;

      return {
        key: point.key || `pt_${index}`,
        cx,
        cyInh,
        cyRes,
        color,
        label: point.label || '',
        tooltip: point.tooltip || point.label || '',
        labelX,
        labelY: anchorY - 3,
        textAnchor: side > 0 ? 'start' : 'end',
      };
    });
  }

  get axisXLabel(): string {
    return this.labelX;
  }

  get axisYLabel(): string {
    return this.labelY;
  }

  svgTickX(v: number): number {
    return svgX(v);
  }

  svgTickY(v: number): number {
    return svgY(v);
  }
}
